import React, { useMemo, useState } from "react";
import {
  AbsoluteFill,
  Img,
  Audio,
  Sequence,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  Easing,
  spring,
} from "remotion";
import { linearTiming, TransitionSeries } from "@remotion/transitions";
import { slide } from "@remotion/transitions/slide";
import { fade } from "@remotion/transitions/fade";
import { wipe } from "@remotion/transitions/wipe";
import { z } from "zod";
import CroppedVideo from "../components/CroppedVideo";
import CroppedImg from "../components/CroppedImg";
import { resolveMediaSrc } from "../utils/mediaSrc";

// ─── Zod Schema ─────────────────────────────────────────────────────────────

const MediaItemSchema = z.object({
  src: z.string().describe("URL or staticFile path for a video or image"),
  type: z.enum(["video", "image"]).optional().describe("Explicit media type from timeline"),
  crop: z
    .object({
      x: z.number(),
      y: z.number(),
      width: z.number(),
      height: z.number(),
      sourceWidth: z.number().optional(),
      sourceHeight: z.number().optional(),
    })
    .nullable()
    .optional()
    .describe("Optional crop rect attached by timeline bridge"),
  label: z
    .string()
    .optional()
    .describe("Optional label overlay for this clip (e.g. 'First Look', 'Inside the Box')"),
  videoStartFrom: z
    .number()
    .default(0)
    .describe("For video type: start playback from this frame (trim start)"),
  videoEndAt: z
    .number()
    .optional()
    .describe("For video type: stop playback at this frame (trim end)"),
});

export const UnboxingOverviewPropsSchema = z.object({
  // Product info
  brandName: z
    .string()
    .default("UNBOXING")
    .describe("Brand or series name displayed in intro"),
  productName: z
    .string()
    .default("Premium Product Review")
    .describe("Product name"),
  tagline: z
    .string()
    .default("Honest First Impressions")
    .describe("Tagline shown in intro/overlay"),

  // Media items – the core adaptive array
  mediaItems: z
    .array(MediaItemSchema)
    .default([
      { src: "", label: "First Look" },
      { src: "", label: "What's Inside" },
      { src: "", label: "The Product" },
      { src: "", label: "Quality Check" },
      { src: "", label: "Final Thoughts" },
    ])
    .describe(
      "Array of media items (video or image). The composition adapts to any count. Optimized for 3 videos + 2 images."
    ),

  // Toggles
  showIntro: z
    .boolean()
    .default(true)
    .describe("Show the intro title card"),
  showOutro: z
    .boolean()
    .default(true)
    .describe("Show the CTA outro"),
  showProgressBar: z
    .boolean()
    .default(true)
    .describe("Show a progress bar across the top"),
  showLabels: z
    .boolean()
    .default(true)
    .describe("Show labels on each media segment"),
  muteMediaItems: z
    .boolean()
    .default(true)
    .describe("Mute audio from media item videos while leaving background audio unchanged"),

  // CTA
  ctaText: z.string().default("FOLLOW FOR MORE").describe("CTA button text"),
  ctaSubtext: z
    .string()
    .default("Link in Bio 🔗")
    .describe("Secondary CTA text"),
  handle: z.string().default("@myhandle").describe("Social media handle"),

  // Timing (in frames at 30fps)
  introDuration: z.number().default(60).describe("Intro card duration in frames"),
  mediaDuration: z
    .number()
    .default(150)
    .describe("Duration per media item in frames (default 5s at 30fps)"),
  outroDuration: z.number().default(75).describe("Outro CTA duration in frames"),
  transitionDuration: z
    .number()
    .default(15)
    .describe("Transition duration between sections in frames"),

  // Style
  accentColor: z.string().default("#FF4D6A").describe("Primary accent color"),
  accentColor2: z.string().default("#FFA726").describe("Secondary accent (warm)"),
  darkBg: z.string().default("#0A0A0A").describe("Dark background color"),
  overlayOpacity: z
    .number()
    .min(0)
    .max(1)
    .default(0.35)
    .describe("Bottom gradient overlay opacity on media"),

  // Audio
  audioSrc: z.string().optional().describe("Optional background audio path"),
  audioVolume: z.number().min(0).max(1).default(0.7).describe("Audio volume"),
  audioStartFrom: z.number().default(0).describe("Audio start in seconds"),
  audioDurationOverride: z
    .number()
    .default(0)
    .describe("If > 0, use this as audio-driven total duration in seconds"),
});

// ─── Helpers ────────────────────────────────────────────────────────────────

const getMediaSrc = (src) => resolveMediaSrc(src);

/** Auto-detect whether a source URL is a video based on file extension */
const isVideoSrc = (src) => {
  if (!src || typeof src !== "string") return false;
  // Strip query strings / fragments then check extension
  const clean = src.split(/[?#]/)[0];
  return /\.(mp4|webm|mov|m4v|avi|mkv|ogv)$/i.test(clean);
};

/** Resolve media type by auto-detecting from src extension */
const resolveMediaType = (item) => {
  if (item?.type === "video" || item?.type === "image") {
    return item.type;
  }
  return isVideoSrc(item.src) ? "video" : "image";
};

const TRANSITION_TYPES = ["slide-left", "slide-right", "fade", "wipe"];

const getTransition = (index) => {
  const type = TRANSITION_TYPES[index % TRANSITION_TYPES.length];
  switch (type) {
    case "slide-left":
      return slide({ direction: "from-left" });
    case "slide-right":
      return slide({ direction: "from-right" });
    case "wipe":
      return wipe({ direction: "from-left" });
    case "fade":
    default:
      return fade();
  }
};

// ─── Sub-components ─────────────────────────────────────────────────────────

/**
 * IntroCard – Quick brand/product title card with kinetic text
 */
const IntroCard = ({ brandName, productName, tagline, accentColor, accentColor2, darkBg }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const brandSpring = spring({ fps, frame, config: { damping: 12, stiffness: 100, mass: 0.6 } });
  const nameSpring = spring({
    fps,
    frame: frame - 8,
    config: { damping: 12, stiffness: 80, mass: 0.7 },
  });
  const tagSpring = spring({
    fps,
    frame: frame - 18,
    config: { damping: 14, stiffness: 60, mass: 0.9 },
  });

  // Line expand
  const lineWidth = interpolate(frame, [12, 35], [0, 280], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: Easing.out(Easing.cubic),
  });

  // Background pulse orb
  const orbScale = interpolate(frame, [0, 60], [0.8, 1.2], { extrapolateRight: "clamp" });

  return (
    <AbsoluteFill
      style={{
        background: darkBg,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      {/* Background glow orb */}
      <div
        style={{
          position: "absolute",
          width: 500,
          height: 500,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${accentColor}20 0%, transparent 70%)`,
          filter: "blur(80px)",
          transform: `scale(${orbScale})`,
        }}
      />

      {/* Unboxing icon / emoji */}
      <div
        style={{
          fontSize: 80,
          marginBottom: 20,
          transform: `scale(${interpolate(brandSpring, [0, 1], [0.3, 1])}) rotate(${interpolate(brandSpring, [0, 1], [-15, 0])}deg)`,
          opacity: brandSpring,
        }}
      >
        📦
      </div>

      {/* Brand name */}
      <div
        style={{
          fontSize: 28,
          fontFamily: "'Inter', 'SF Pro Display', system-ui, sans-serif",
          fontWeight: 300,
          letterSpacing: 12,
          color: accentColor,
          textTransform: "uppercase",
          transform: `translateY(${interpolate(brandSpring, [0, 1], [40, 0])}px)`,
          opacity: brandSpring,
          marginBottom: 16,
        }}
      >
        {brandName}
      </div>

      {/* Product name */}
      <div
        style={{
          fontSize: 72,
          fontFamily: "'Inter', 'SF Pro Display', system-ui, sans-serif",
          fontWeight: 900,
          color: "#ffffff",
          textAlign: "center",
          lineHeight: 1.1,
          padding: "0 60px",
          transform: `translateY(${interpolate(nameSpring, [0, 1], [50, 0])}px)`,
          opacity: Math.max(0, nameSpring),
        }}
      >
        {productName}
      </div>

      {/* Accent line */}
      <div
        style={{
          width: lineWidth,
          height: 4,
          background: `linear-gradient(90deg, ${accentColor}, ${accentColor2})`,
          marginTop: 24,
          borderRadius: 2,
        }}
      />

      {/* Tagline */}
      <div
        style={{
          fontSize: 30,
          fontFamily: "'Inter', system-ui, sans-serif",
          fontWeight: 400,
          color: "#ffffffaa",
          marginTop: 20,
          fontStyle: "italic",
          transform: `translateY(${interpolate(tagSpring, [0, 1], [30, 0])}px)`,
          opacity: Math.max(0, tagSpring),
        }}
      >
        {tagline}
      </div>
    </AbsoluteFill>
  );
};

/**
 * MediaSegment – Renders a single video or image with Ken Burns, label overlay, and segment counter
 */
const MediaSegment = ({
  item,
  index,
  totalItems,
  accentColor,
  accentColor2,
  darkBg,
  overlayOpacity,
  showLabel,
  showProgressBar,
  mediaDuration,
  crop,
  muteMediaItems,
}) => {
  const [videoError, setVideoError] = useState("");
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  const src = getMediaSrc(item.src);
  const isVideo = resolveMediaType(item) === "video";

  // Ken Burns for images: slow zoom + pan
  const zoom = interpolate(frame, [0, mediaDuration], [1.0, 1.12], {
    extrapolateRight: "clamp",
  });
  const panX = interpolate(frame, [0, mediaDuration], [0, index % 2 === 0 ? -1.5 : 1.5], {
    extrapolateRight: "clamp",
  });
  const panY = interpolate(frame, [0, mediaDuration], [0, index % 3 === 0 ? -1 : 0.8], {
    extrapolateRight: "clamp",
  });

  // Entry animation
  const entryOpacity = interpolate(frame, [0, 8], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Label spring
  const labelSpring = spring({
    fps,
    frame: frame - 10,
    config: { damping: 12, stiffness: 100, mass: 0.5 },
  });

  // Segment counter spring
  const counterSpring = spring({
    fps,
    frame: frame - 5,
    config: { damping: 14, stiffness: 90, mass: 0.5 },
  });

  // Progress within this segment (0-1)
  const segmentProgress = interpolate(frame, [0, mediaDuration], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill style={{ background: darkBg, overflow: "hidden" }}>
      {/* Media content */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          opacity: entryOpacity,
        }}
      >
        {isVideo && src && !videoError ? (
          <CroppedVideo
            src={src}
            crop={crop}
            startFrom={item.videoStartFrom || 0}
            endAt={item.videoEndAt}
            muted={muteMediaItems}
            onError={(error) => {
              const message = error?.error?.message || error?.message || "Unknown video playback error";
              setVideoError(`${message} (src: ${src})`);
            }}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
            containerStyle={{
              width: "100%",
              height: "100%",
            }}
          />
        ) : isVideo && videoError ? (
          <div
            style={{
              width: "100%",
              height: "100%",
              background: "#111",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: 24,
              textAlign: "center",
              color: "#fff",
              fontFamily: "Arial, sans-serif",
            }}
          >
            <div style={{ fontSize: 26, marginBottom: 8 }}>Video failed to load</div>
            <div style={{ fontSize: 14, color: "#b3b3b3", maxWidth: 900 }}>{videoError}</div>
          </div>
        ) : src ? (
          <CroppedImg
            src={src}
            crop={crop}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              transform: `scale(${zoom}) translate(${panX}%, ${panY}%)`,
            }}
            containerStyle={{
              width: "100%",
              height: "100%",
            }}
          />
        ) : (
          /* Placeholder when no src */
          <div
            style={{
              width: "100%",
              height: "100%",
              background: `linear-gradient(135deg, ${darkBg}, #1a1a2e, ${darkBg})`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 48,
              color: "#ffffff33",
              fontFamily: "'Inter', system-ui, sans-serif",
            }}
          >
            {isVideo ? "🎬" : "🖼️"} {index + 1}
          </div>
        )}
      </div>

      {/* Bottom gradient overlay */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "45%",
          background: `linear-gradient(180deg, transparent 0%, ${darkBg}cc 60%, ${darkBg} 100%)`,
          opacity: overlayOpacity,
          pointerEvents: "none",
        }}
      />

      {/* Top gradient overlay (subtle) */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "15%",
          background: `linear-gradient(0deg, transparent 0%, ${darkBg}88 100%)`,
          opacity: overlayOpacity * 0.6,
          pointerEvents: "none",
        }}
      />

      {/* Segment progress bar (top) */}
      {showProgressBar && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            display: "flex",
            gap: 4,
            padding: "8px 16px 0 16px",
            zIndex: 10,
          }}
        >
          {Array.from({ length: totalItems }, (_, i) => {
            let fill;
            if (i < index) fill = 1;
            else if (i === index) fill = segmentProgress;
            else fill = 0;

            return (
              <div
                key={i}
                style={{
                  flex: 1,
                  height: 4,
                  borderRadius: 2,
                  background: "#ffffff33",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${fill * 100}%`,
                    height: "100%",
                    background: `linear-gradient(90deg, ${accentColor}, ${accentColor2})`,
                    borderRadius: 2,
                  }}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* Segment counter badge (top-right) */}
      <div
        style={{
          position: "absolute",
          top: 28,
          right: 24,
          background: "rgba(0,0,0,0.5)",
          backdropFilter: "blur(10px)",
          borderRadius: 20,
          padding: "8px 16px",
          display: "flex",
          alignItems: "center",
          gap: 6,
          transform: `scale(${interpolate(counterSpring, [0, 1], [0.5, 1])})`,
          opacity: Math.max(0, counterSpring),
          zIndex: 10,
        }}
      >
        <span
          style={{
            fontSize: 18,
            fontFamily: "'Inter', system-ui, sans-serif",
            fontWeight: 700,
            color: accentColor,
          }}
        >
          {index + 1}
        </span>
        <span
          style={{
            fontSize: 16,
            fontFamily: "'Inter', system-ui, sans-serif",
            fontWeight: 400,
            color: "#ffffff88",
          }}
        >
          / {totalItems}
        </span>
      </div>

      {/* Label overlay (bottom) */}
      {showLabel && item.label && (
        <div
          style={{
            position: "absolute",
            bottom: 100,
            left: 40,
            right: 40,
            zIndex: 10,
          }}
        >
          {/* Label pill */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 12,
              background: `linear-gradient(135deg, ${accentColor}dd, ${accentColor2}dd)`,
              padding: "14px 28px",
              borderRadius: 16,
              transform: `translateY(${interpolate(labelSpring, [0, 1], [30, 0])}px)`,
              opacity: Math.max(0, labelSpring),
              boxShadow: `0 8px 32px ${accentColor}44`,
            }}
          >
            <span
              style={{
                fontSize: 28,
                fontFamily: "'Inter', 'SF Pro Display', system-ui, sans-serif",
                fontWeight: 700,
                color: "#fff",
                letterSpacing: 1,
              }}
            >
              {item.label}
            </span>
          </div>
        </div>
      )}
    </AbsoluteFill>
  );
};

/**
 * CTAOutro – Follow / Link in Bio
 */
const CTAOutro = ({
  ctaText,
  ctaSubtext,
  handle,
  accentColor,
  accentColor2,
  darkBg,
  brandName,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const ctaSpring = spring({
    fps,
    frame: frame - 5,
    config: { damping: 10, stiffness: 90, mass: 0.7 },
  });
  const handleSpring = spring({
    fps,
    frame: frame - 20,
    config: { damping: 14, stiffness: 60, mass: 1 },
  });

  const pulseGlow = 0.4 + 0.3 * Math.sin((frame * Math.PI) / 15);
  const bgAngle = interpolate(frame, [0, 75], [135, 225], { extrapolateRight: "clamp" });

  return (
    <AbsoluteFill
      style={{
        background: `linear-gradient(${bgAngle}deg, ${darkBg}, #1a0a1e, ${darkBg})`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      {/* Decorative orbs */}
      <div
        style={{
          position: "absolute",
          width: 500,
          height: 500,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${accentColor}18 0%, transparent 70%)`,
          top: "10%",
          left: "-10%",
          filter: "blur(60px)",
        }}
      />
      <div
        style={{
          position: "absolute",
          width: 400,
          height: 400,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${accentColor2}15 0%, transparent 70%)`,
          bottom: "10%",
          right: "-5%",
          filter: "blur(50px)",
        }}
      />

      {/* Brand */}
      <div
        style={{
          fontSize: 26,
          fontFamily: "'Inter', system-ui, sans-serif",
          fontWeight: 300,
          letterSpacing: 10,
          color: "#ffffff55",
          textTransform: "uppercase",
          marginBottom: 30,
          opacity: interpolate(frame, [0, 12], [0, 1], { extrapolateRight: "clamp" }),
        }}
      >
        {brandName}
      </div>

      {/* Verdict / recap icon */}
      <div
        style={{
          fontSize: 64,
          marginBottom: 24,
          opacity: interpolate(frame, [3, 15], [0, 1], { extrapolateRight: "clamp" }),
        }}
      >
        ✅
      </div>

      {/* CTA Button */}
      <div
        style={{
          transform: `scale(${interpolate(ctaSpring, [0, 1], [0.5, 1])})`,
          opacity: Math.max(0, ctaSpring),
        }}
      >
        <div
          style={{
            background: `linear-gradient(135deg, ${accentColor}, ${accentColor2})`,
            padding: "24px 70px",
            borderRadius: 60,
            fontSize: 38,
            fontFamily: "'Inter', 'SF Pro Display', system-ui, sans-serif",
            fontWeight: 800,
            color: "#fff",
            letterSpacing: 3,
            textTransform: "uppercase",
            boxShadow: `0 0 ${55 * pulseGlow}px ${accentColor}88, 0 10px 36px rgba(0,0,0,0.4)`,
          }}
        >
          {ctaText}
        </div>
      </div>

      {/* Subtext */}
      <div
        style={{
          fontSize: 28,
          fontFamily: "'Inter', system-ui, sans-serif",
          fontWeight: 500,
          color: "#ffffffaa",
          marginTop: 24,
          opacity: interpolate(frame, [12, 25], [0, 1], { extrapolateRight: "clamp" }),
        }}
      >
        {ctaSubtext}
      </div>

      {/* Handle */}
      <div
        style={{
          fontSize: 26,
          fontFamily: "'Inter', system-ui, sans-serif",
          fontWeight: 600,
          background: `linear-gradient(90deg, ${accentColor}, ${accentColor2})`,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: "text",
          marginTop: 40,
          transform: `translateY(${interpolate(handleSpring, [0, 1], [25, 0])}px)`,
          opacity: Math.max(0, handleSpring),
        }}
      >
        {handle}
      </div>
    </AbsoluteFill>
  );
};

// ─── Main Component ─────────────────────────────────────────────────────────

const UnboxingOverview = ({
  brandName = "UNBOXING",
  productName = "Premium Product Review",
  tagline = "Honest First Impressions",
  mediaItems = [],
  showIntro = true,
  showOutro = true,
  showProgressBar = true,
  showLabels = true,
  muteMediaItems = true,
  ctaText = "FOLLOW FOR MORE",
  ctaSubtext = "Link in Bio 🔗",
  handle = "@myhandle",
  introDuration = 60,
  mediaDuration = 150,
  outroDuration = 75,
  transitionDuration = 15,
  accentColor = "#FF4D6A",
  accentColor2 = "#FFA726",
  darkBg = "#0A0A0A",
  overlayOpacity = 0.35,
  audioSrc = "",
  audioVolume = 0.7,
  audioStartFrom = 0,
  audioDurationOverride = 0,
  _crops = {},
}) => {
  const [audioError, setAudioError] = useState(false);
  const audioSource = useMemo(() => {
    if (!audioSrc) return null;
    try {
      return audioSrc.startsWith("http") ? audioSrc : staticFile(audioSrc);
    } catch {
      return null;
    }
  }, [audioSrc]);

  // Filter out items with no src if desired, or keep all for placeholder rendering
  const items = mediaItems.length > 0 ? mediaItems : [{ src: "", type: "video", label: "Clip 1" }];

  return (
    <AbsoluteFill style={{ background: darkBg }}>
      {/* Global audio */}
      {audioSource && !audioError && (
        <Audio
          src={audioSource}
          volume={audioVolume}
          startFrom={audioStartFrom * 30}
          onError={(error) => {
            console.warn("UnboxingOverview audio failed to load", error);
            setAudioError(true);
          }}
        />
      )}

      <TransitionSeries>
        {/* 1. Intro (optional) */}
        {showIntro && (
          <>
            <TransitionSeries.Sequence durationInFrames={introDuration}>
              <IntroCard
                brandName={brandName}
                productName={productName}
                tagline={tagline}
                accentColor={accentColor}
                accentColor2={accentColor2}
                darkBg={darkBg}
              />
            </TransitionSeries.Sequence>
            <TransitionSeries.Transition
              presentation={fade()}
              timing={linearTiming({ durationInFrames: transitionDuration })}
            />
          </>
        )}

        {/* 2. Media segments – dynamically mapped */}
        {items.map((item, index) => (
          <React.Fragment key={index}>
            {/* Transition between media segments (skip before first if no intro) */}
            {index > 0 && (
              <TransitionSeries.Transition
                presentation={getTransition(index)}
                timing={linearTiming({ durationInFrames: transitionDuration })}
              />
            )}
            <TransitionSeries.Sequence durationInFrames={mediaDuration}>
              <MediaSegment
                item={item}
                index={index}
                totalItems={items.length}
                accentColor={accentColor}
                accentColor2={accentColor2}
                darkBg={darkBg}
                overlayOpacity={overlayOpacity}
                showLabel={showLabels}
                showProgressBar={showProgressBar}
                mediaDuration={mediaDuration}
                muteMediaItems={muteMediaItems}
                crop={item?.crop ?? (Array.isArray(_crops.mediaItems) ? _crops.mediaItems[index] : null)}
              />
            </TransitionSeries.Sequence>
          </React.Fragment>
        ))}

        {/* 3. Outro (optional) */}
        {showOutro && (
          <>
            <TransitionSeries.Transition
              presentation={fade()}
              timing={linearTiming({ durationInFrames: transitionDuration })}
            />
            <TransitionSeries.Sequence durationInFrames={outroDuration}>
              <CTAOutro
                ctaText={ctaText}
                ctaSubtext={ctaSubtext}
                handle={handle}
                accentColor={accentColor}
                accentColor2={accentColor2}
                darkBg={darkBg}
                brandName={brandName}
              />
            </TransitionSeries.Sequence>
          </>
        )}
      </TransitionSeries>
    </AbsoluteFill>
  );
};

// ─── Calculate Metadata ─────────────────────────────────────────────────────

export const calculateUnboxingOverviewMetadata = async ({ props }) => {
  const {
    showIntro = true,
    showOutro = true,
    introDuration = 60,
    mediaDuration = 150,
    outroDuration = 75,
    transitionDuration = 15,
    mediaItems = [],
    audioSrc,
    audioDurationOverride = 0,
    audioStartFrom = 0,
  } = props;

  const itemCount = Math.max((mediaItems || []).length, 1);

  // Total frames = sum of all section durations minus overlapping transitions
  let totalFrames = itemCount * mediaDuration;
  let transitionCount = 0;

  // Transitions between media items
  if (itemCount > 1) {
    transitionCount += itemCount - 1;
  }

  if (showIntro) {
    totalFrames += introDuration;
    transitionCount += 1; // intro → first media
  }

  if (showOutro) {
    totalFrames += outroDuration;
    transitionCount += 1; // last media → outro
  }

  totalFrames -= transitionCount * transitionDuration;

  // Audio override
  if (audioSrc && audioDurationOverride > 0) {
    const audioFrames = Math.ceil((audioDurationOverride - audioStartFrom) * 30);
    return { durationInFrames: Math.max(60, audioFrames) };
  }

  return { durationInFrames: Math.max(60, totalFrames) };
};

export default UnboxingOverview;

;
    var _remotion_globalVariableA, _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
